import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  Phone, 
  MessageSquare, 
  Users, 
  HardDrive,
  Pause,
  Play,
  Trash2,
  UserCheck,
} from "lucide-react";
import { format } from "date-fns";

interface Tenant {
  tenant_id: string;
  name: string;
  slug: string | null;
  domain: string | null;
  status: string | null;
  plan_id: string | null;
  max_users: number | null;
  max_phone_numbers: number | null;
  max_sms_per_day: number | null;
  billing_email: string | null;
  billing_via_agency: boolean | null;
  created_at: string;
  updated_at: string;
  user_count?: number;
  usage_stats?: {
    calls_today: number;
    sms_today: number;
    storage_mb: number;
    phone_numbers: number;
  };
}

interface CustomerOverviewTabProps {
  tenant: Tenant;
  onSuspend: () => void;
  onReactivate: () => void;
  onDelete: () => void;
  onImpersonate: () => void;
  isLoading: boolean;
}

const statusColors: Record<string, string> = {
  active: "bg-green-500/10 text-green-500 border-green-500/20",
  suspended: "bg-red-500/10 text-red-500 border-red-500/20",
  pending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
  deleted: "bg-muted text-muted-foreground border-muted",
};

export function CustomerOverviewTab({
  tenant,
  onSuspend,
  onReactivate,
  onDelete,
  onImpersonate,
  isLoading,
}: CustomerOverviewTabProps) {
  const isSuspended = tenant.status === "suspended";
  const usageStats = tenant.usage_stats || {
    calls_today: 0,
    sms_today: 0,
    storage_mb: 0,
    phone_numbers: 0,
  };

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Customer Information</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-muted-foreground">Business Name</p>
            <p className="font-medium">{tenant.name}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Slug</p>
            <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
              {tenant.slug || "-"}
            </code>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Domain</p>
            <p className="font-medium">{tenant.domain || "-"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Billing Email</p>
            <p className="font-medium">{tenant.billing_email || "-"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Plan</p>
            <p className="font-medium">{tenant.plan_id || "Default"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Billing</p>
            <p className="font-medium">
              {tenant.billing_via_agency ? "Via Agency" : "Direct"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Created</p>
            <p className="font-medium">
              {format(new Date(tenant.created_at), "MMM d, yyyy")}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Last Updated</p>
            <p className="font-medium">
              {format(new Date(tenant.updated_at), "MMM d, yyyy")}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Usage Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Calls Today</span>
            </div>
            <p className="text-2xl font-bold mt-2">{usageStats.calls_today}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">SMS Today</span>
            </div>
            <p className="text-2xl font-bold mt-2">{usageStats.sms_today}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Users</span>
            </div>
            <p className="text-2xl font-bold mt-2">{tenant.user_count || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Phone Numbers</span>
            </div>
            <p className="text-2xl font-bold mt-2">{usageStats.phone_numbers}</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={onImpersonate}
            disabled={isLoading || tenant.status === "deleted"}
          >
            <UserCheck className="mr-2 h-4 w-4" />
            Impersonate
          </Button>

          {isSuspended ? (
            <Button
              variant="outline"
              onClick={onReactivate}
              disabled={isLoading}
            >
              <Play className="mr-2 h-4 w-4" />
              Reactivate
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={onSuspend}
              disabled={isLoading || tenant.status === "deleted"}
            >
              <Pause className="mr-2 h-4 w-4" />
              Suspend
            </Button>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                disabled={isLoading || tenant.status === "deleted"}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Customer</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{tenant.name}"? This action
                  cannot be undone. The customer will lose access to their account
                  and all associated data.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
